import { Box, Card, CardContent, Divider, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import PermissionGuard from 'components/PermissionGuard';
import { TextV2 } from '@insights-ltd/design-library/components';
import DefaultIcon from 'components/Svgs/icons/Organisation';
import { OrganisationGroup } from 'api/httpEntities';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { useTranslation } from 'react-i18next';
import OrganisationCardLink from './OrganisationCardLink';
import ParentGroupLink from './ParentGroupLink';

type OrganisationCardProps = {
  id: string;
  name: string;
  group?: OrganisationGroup;
};

const StyledDefaultIcon = styled(DefaultIcon)(({ theme }) => ({
  position: 'relative',
  top: '24px',
  width: '48px',
  height: '48px',
  padding: '5px 4px',
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.grey[400]}`,
  borderRadius: '5px',
  fill: theme.palette.grey[500],
}));

const OrganisationCard: React.FC<OrganisationCardProps> = ({
  id,
  name,
  group,
}) => {
  const { t } = useTranslation();

  return (
    <Grid
      key={id}
      sx={{
        '&:nth-of-type(-n + 3)': { minHeight: '316px' },
        '&:nth-of-type(n + 4)': { minHeight: '300px' },
      }}
      size={{ xs: 12, md: 4 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          alignItems: 'center',
          justifyContent: 'flex-start',
          textAlign: 'center',
          marginTop: 0,
        }}
      >
        <StyledDefaultIcon
          title={t('ui.event-management.organisations.alt.icon')}
        />
      </Box>
      <Card
        variant="outlined"
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: 'calc(100% - 48px)',
          padding: `0 ${theme.spacing(spacingSizeMap.XS)} ${theme.spacing(
            spacingSizeMap.S,
          )} ${theme.spacing(spacingSizeMap.XS)}`,
          borderColor: theme.palette.grey[500],
        })}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              alignItems: 'center',
              justifyContent: 'flex-start',
              textAlign: 'center',
              marginTop: 0,
            }}
          >
            <TextV2
              sx={(theme) => ({
                margin: `${theme.spacing(spacingSizeMap.S)} ${theme.spacing(
                  spacingSizeMap.S,
                )} 0  !important`,
                wordBreak: 'break-word',
              })}
              component="h1"
              variant="h3"
            >
              {name}
            </TextV2>
            {group ? (
              <>
                <ParentGroupLink
                  group={group}
                  sx={{ lineHeight: '28px !important', margin: '0.5rem 0 0' }}
                />
                <Divider
                  sx={(theme) => ({
                    border: `0.5px solid ${theme.palette.grey[400]}`,
                    width: '5rem',
                    margin: `12px 0 0`,
                  })}
                />
              </>
            ) : null}
            <Box component="span" sx={{ marginTop: 1 }}>
              <OrganisationCardLink
                to={`/organisations/${id}`}
                state={{ activeTab: 'practitioners' }}
              >
                {t('ui.event-management.organisations.link.users')}
              </OrganisationCardLink>
              <OrganisationCardLink
                to={`/organisations/${id}`}
                state={{ activeTab: 'upcomingEvents' }}
              >
                {t('ui.event-management.organisations.link.experiences')}
              </OrganisationCardLink>
              <PermissionGuard
                requiredPermissions={[
                  {
                    action: 'Organisation_ReadAll',
                    scope: 'Global',
                  },
                ]}
              >
                <OrganisationCardLink
                  to={`/organisations/${id}`}
                  state={{ activeTab: 'invites' }}
                >
                  {t('ui.event-management.organisations.link.invites')}
                </OrganisationCardLink>
              </PermissionGuard>
              <OrganisationCardLink
                to={`/learners`}
                state={{ organisation: { id, name } }}
              >
                {t('ui.event-management.organisation.learners-and-teams.link')}
              </OrganisationCardLink>
              <OrganisationCardLink
                to={`/evaluator-links`}
                state={{ organisation: { id, name } }}
              >
                {t('ui.event-management.organisations.link.evaluator-links')}
              </OrganisationCardLink>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default OrganisationCard;
